"use client";

import React, { useState } from "react";
import { SpacingType, sectionVariants, ButtonStyleType } from "@/types";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import parse from "html-react-parser";
import { VideoIframe, useVideo } from "@/components/components/VideoIframe";
import Image from '@/components/ui/Image';
import { cn } from "@/lib/utils";
// import { AuroraText } from "@/components/magicui/aurora-text";

interface Hero19Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
    hasStatus?: boolean;
    status?: {
      value?: string;
      label?: string;
    }[];
    hasFeaturedItems?: boolean;
    featuredItems?: {
      icon?: string;
      content?: string;
    }[];
    featuredImage1?: string;
    featuredImage1AltText?: string;
    featuredItem?: "image" | "video";
    video?: string;
    loopVideo?: string;
    image?: string;
    imageAltText?: string;
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero19 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  attributes,
}: Hero19Props) => {

  const { isVideoOpen, currentVideo, getVideoThumbnail, handleVideoClick, handleCloseVideo } = useVideo();
  const [videoError, setVideoError] = useState(false);

  const getVideoType = (url: string) => {
    const extension = url.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'mp4':
        return 'video/mp4';
      case 'webm':
        return 'video/webm';
      case 'ogg':
        return 'video/ogg';
      case 'mov':
        return 'video/quicktime';
      case 'avi':
        return 'video/x-msvideo';
      default:
        return 'video/mp4'; // Default fallback
    }
  };

  return (
    <SectionWrapper 
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero19"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 19"
      removePaddingBottom={true}
      className="bg-position-[50%_100%] overflow-hidden"  
      style={{
        backgroundImage: "url('/hero-bottom-coloured-bg.svg')",
        backgroundSize: "auto",
        backgroundRepeat: "no-repeat",
      }}
    >     
      <div className="container"> 
        <div className="w-full text-center lg:text-left">
          {contents?.badge && (
            <Badge variant="outline" bold className="mb-6">
              {contents?.badge}
            </Badge>
          )}
          {contents?.title && (
            <h1 className="mb-10 lg:mb-16">
              {parse(contents?.title)}
            </h1>
          )}
        </div>

        <div className="grid grid-cols-1 gap-x-30 xl:gap-x-56 gap-y-16 lg:grid-cols-2">
          <div className="flex flex-col gap-12 flex-1 h-full justify-between">
            <div className="flex flex-col gap-12 text-center lg:text-left items-center lg:items-start">
              {contents?.content && (
                <div className="paragraph-lg max-w-[52ch]">
                  {parse(contents?.content)}
                </div>
              )}
              {contents?.hasButton && (
                <div className="flex flex-col">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <div>
                      <Button 
                        size="lg" 
                        variant={contents?.buttonStyle || "primary"}
                        className="w-full gap-2 sm:w-auto"
                        asChild
                      >
                        <a href={contents?.buttonLink}>
                          {contents?.buttonText}
                        </a>
                      </Button>
                      {contents?.microCopy && (
                        <div className="paragraph-sm text-muted-foreground text-center lg:text-left mt-2">
                          {contents?.microCopy}
                        </div>
                      )}
                    </div>
                    {contents?.secondaryButton && (
                      <Button
                        variant={contents?.secondaryButtonStyle || "outline"}
                        size="lg"
                        className="w-full gap-2 sm:w-auto"
                        asChild
                      >
                        <a href={contents?.secondaryButtonLink}>
                          {contents?.secondaryButtonText}
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="w-full lg:w-[300px] pb-4 lg:pb-20">
              <div className={cn("relative", contents?.featuredItem === "video" && "cursor-pointer")} onClick={() => contents?.featuredItem === "video" && handleVideoClick(contents?.video || "")}>
                <div className="hidden lg:block text-muted-foreground paragraph-base mb-4">
                  How we power Australian Businesses
                </div>
                {contents?.loopVideo && (
                  <>
                    <video 
                      preload="none" 
                      autoPlay 
                      loop 
                      muted 
                      playsInline 
                      className={cn(
                        "object-cover aspect-[16/9] rounded-xl w-full h-full",
                        videoError && "hidden"
                      )}
                      onError={() => setVideoError(true)}
                    >
                      <source src={contents?.loopVideo} type={getVideoType(contents?.loopVideo)} />
                    </video>
                    <Image
                      src={contents?.image || "/placeholder.svg"}
                      alt={contents?.imageAltText || ""}
                      title={contents?.imageAltText || ""}
                      className={cn(
                        "h-full w-full object-cover aspect-[16/9] rounded-xl",
                        !videoError && "hidden"
                      )}
                      width={800}
                      height={450}
                      priority
                      fetchPriority="high"
                    />
                  </>
                )}
                {contents?.image && !contents?.loopVideo && (
                  <Image
                    src={contents?.featuredItem === "video" ? contents?.image || getVideoThumbnail(contents?.video || "") || '/placeholder.svg' : contents?.image || "/placeholder.svg"}
                    alt={contents?.imageAltText || ""}
                    title={contents?.imageAltText || ""}
                    className="h-full w-full object-cover aspect-[16/9] rounded-xl"
                    width={800}
                    height={450}
                    priority
                    fetchPriority="high"
                  />
                )}
                {contents?.featuredItem === "video" && (
                  <> 
                    <div className="absolute flex left-[16px] bottom-[16px] w-14 h-14">
                      <Button 
                        variant="secondary" 
                        size="iconLg"   
                        className="border-none bg-muted rounded-full w-14 h-14"
                      >
                        <span className="material-symbols-outlined font-variation-filled">play_arrow</span>
                      </Button>
                    </div>
                    <VideoIframe 
                      videoUrl={currentVideo} 
                      isOpen={isVideoOpen} 
                      onClose={handleCloseVideo} 
                    />
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="relative min-w-[1170px] max-w-[1170px] rounded-xl -mb-10 h-[640px]">
              {contents?.featuredImage1 && (

                <Image
                  src={contents?.featuredImage1}
                  alt={contents?.featuredImage1AltText || ""}
                  title={contents?.featuredImage1AltText || ""}
                  className="h-full w-full object-cover object-left-top  aspect-[1170/826] rounded-xl"
                  width={1170}
                  height={826}
                  priority
                  fetchPriority="high"
                />

                )}
            </div>
          </div>
        </div>  

      </div>
    </SectionWrapper>
  );
};

export { Hero19 };


